import { faCircleCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import invariant from "ts-invariant";

import type { UserCourseLesson } from "api/schemas/UserCourse";
import type { ExerciseListItem } from "api/services/content/schemas/exercises";
import { useProfileContext } from "contexts/ProfileContext";
import { Avatar } from "shared/atoms/Avatar/Avatar";
import { BaseButton } from "shared/atoms/BaseButton";

import { ContentContainer, StyledLibraryItem } from "../components/styles";

import type { Category } from "./Library";

type ExtendedListItem = (ExerciseListItem | UserCourseLesson) & {
  new?: boolean;
};

export type LocationState = {
  action: "created" | "edited";
  type: Category;
  item: ExtendedListItem;
};

export const Confirmation: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { state } = useLocation() as { state: LocationState };

  const { profile } = useProfileContext();

  const action = state?.action || "created";
  const item = state?.item;
  const type = state?.type || "exercises";

  invariant(profile, "Profile is required");

  const isExerciseListItem = (itemToCheck: ExtendedListItem): itemToCheck is ExerciseListItem & { new?: boolean } => {
    return "levels" in itemToCheck && Array.isArray(itemToCheck.levels);
  };

  const imageUrl = isExerciseListItem(item) ? item.levels?.[0]?.image_url : (item as UserCourseLesson)?.image_url;
  const title = isExerciseListItem(item) ? item.levels?.[0]?.title : (item as UserCourseLesson)?.title;

  return (
    <ContentContainer>
      <Container>
        <FontAwesomeIcon
          icon={faCircleCheck}
          color={theme.colors.redesign.g100}
          style={{ fontSize: "80px", marginBottom: "12px" }}
        />
        <Heading>{t("library.confirmation", { action, type })}</Heading>
        <StyledLibraryItem
          author={{
            avatar: (
              <Avatar
                id={profile?.id}
                size={40}
                src={profile?.avatars?.medium || ""}
                name={`${profile?.first_name} ${profile?.last_name}`}
              />
            ),
            name: `${profile?.first_name} ${profile?.last_name}`,
          }}
          checked={false}
          image={imageUrl ? <img alt="" src={imageUrl} height="100%" /> : null}
          tags={item?.target_joints}
          title={title}
        />
        <BaseButton
          text={t("buttons.view_in_library")}
          onClick={() => navigate(`/library/your-library/${type}`, { state: { item } })}
        />
      </Container>
    </ContentContainer>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;

  & ${StyledLibraryItem} {
    max-width: 450px;
    margin: 24px 0;
  }
`;

const Heading = styled.h2`
  font-size: 28px;
  font-style: normal;
  font-weight: 600;
  line-height: 34px;
  letter-spacing: 0.1px;
  margin: 0;
`;
